import React, { useEffect, useState, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Notification from './Notification';
import CookiesNotification from './CookiesNotification';
import Navigation from './Navigation';
import SearchForm from './SearchForm';
import SearchResults from './SearchResults';
import Cart from '../Selection/Cart';
import Select from '../Elements/Select/Select';
import { translate, prefix } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset } from '../../utils/dom';
import { cleanInternalLinks } from '../../utils/string';
import { removeNotification, resetFilter, changeLanguage, goToPage, changeDevice, setCookieConsent } from '../../actions/environment';
import { clientInit, changeCountry, changeAddressInput } from '../../actions/checkout';
import s from './Header.scss';

const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };

const Header = ({ setup, properties, header = {}, navigation = {}, checkout = {}, location, pageStatus, ...props }) => {
	const [grid, toggleGrid] = useState(false);
	const [cartOpen, toggleCartOpen] = useState(false);
	const [menuPopupHeight, setMenuPopupHeight] = useState('auto');
	const [showCountry, toggleShowCountry] = useState(false);
	const [showAssistance, toggleShowAssistance] = useState(false);
	const [showSearch, toggleShowSearch] = useState(location.type === 'SEARCH');
	const [showSearchSuggestions, toggleShowSearchSuggestions] = useState(location.type === 'SEARCH');

	const headerElement = useRef(null);
	const cartElement = useRef(null);

	useEffect(() => {
		const $html = document.querySelector('html');
		let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
		let device = media[`w_${currentWidth}`];

		props.clientInit();
		props.changeDevice(device);

		// Wait for page to be rendered with all relevant elements.
		const initiateSiteListeners = () => {
			if (!headerElement.current || !cartElement.current) {
				setTimeout(initiateSiteListeners, 200);
			}

			let isScrolled = false;
			let headerIsFixed = false;
			let isScrollingUp = false;
			let lastScrolledDown = 0;
			let lastScrolledUp = 1e5;

			const menuOffsetTemp = offset(headerElement.current).top;
			setMenuPopupHeight((prevState) => {
				if (prevState === 'auto') return window.innerHeight - 40 - (menuOffsetTemp || 0);
				return prevState;
			});

			const resizeEvent = () => {
				const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);

				// Switch between mobile, table and desktop.
				if (currentWidth !== updatedWidth) {
					device = media[`w_${updatedWidth}`];
					props.changeDevice(device);
					currentWidth = updatedWidth;
				}
			};

			addEvent(window, 'resize.logoHeader', () => {
				window.requestAnimationFrame(resizeEvent);
			});

			const scrollEvent = () => {
				const top = window.pageYOffset || document.documentElement.scrollTop;

				toggleShowSearchSuggestions(false);

				// make logo sticky when level with header.
				if (!headerIsFixed && top >= menuOffsetTemp) {
					headerIsFixed = true;
					addClass($html, 'u_headerIsFixed');
					setMenuPopupHeight(window.innerHeight - 40);
				} else if (headerIsFixed && top < menuOffsetTemp) {
					headerIsFixed = false;
					removeClass($html, 'u_headerIsFixed');
					setMenuPopupHeight(window.innerHeight - 40 - (menuOffsetTemp || 0));
				}

				if (top > 0 && !isScrolled) {
					addClass($html, 'u_pageIsScrolled');
					isScrolled = true;
				} else if (top === 0) {
					removeClass($html, 'u_pageIsScrolled');
					isScrolled = false;
					headerIsFixed = false;
				}

				if (top > lastScrolledDown) lastScrolledDown = top;
				if ((top < lastScrolledUp && isScrollingUp) || !isScrollingUp) lastScrolledUp = top;

				// console.log('DEBUG: lastScrolledDown, lastScrolledUp, top', lastScrolledDown, lastScrolledUp, top, isScrollingUp);

				if (lastScrolledDown - top > 40 && !isScrollingUp) {
					addClass($html, 'u_pageIsScrollingUp');
					isScrollingUp = true;
				}
				if (top - lastScrolledUp > 40 && isScrollingUp) {
					removeClass($html, 'u_pageIsScrollingUp');
					lastScrolledDown = top;
					isScrollingUp = false;
				}
			};

			addEvent(window, 'scroll.logoHeader', () => {
				window.requestAnimationFrame(scrollEvent);
			});
		};

		initiateSiteListeners();

		if ('ontouchstart' in document.documentElement) {
			removeClass($html, 'no-touchevents');
			addClass($html, 'touchevents');
		}

		if (hasClass($html, 'no-touchevents')) {
			addEvent(window, 'touchstart.touchTest', () => {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
				removeEvent(window, 'touchstart.touchTest');
			});
		}

		addEvent(document.querySelector('body'), 'click.linkCapture', (e) => {
			const $link = e.target.closest('a');

			/*
			if ($link && this.props.checkout.klarnaUnloaded && $link.getAttribute('href').indexOf('#') !== 0) {
				e.preventDefault();
				e.stopPropagation();
				window.location = $link.href;
				// Force full page reload to clear Klarna on next page click.
			} else
			*/
			if ($link && hasClass($link, 'linkCapture')) {
				e.preventDefault();
				props.goToPage($link.getAttribute('href'));
			}
		});

		addEvent(window, 'keydown.general', (e) => {
			if (e.key === 'Escape') {
				toggleCartOpen(false);
				toggleGrid(false);
				toggleShowSearch(false);
				toggleShowSearchSuggestions(false);
			} else if (e.shiftKey && e.ctrlKey && e.key === 'A') {
				// ctrl + shift + a = Toggle grid
				toggleGrid((prevState) => !prevState);
			}
		});

		return () => {
			removeEvent(document.querySelector('body'), 'click.cartClose');
			removeEvent(window, 'keydown.general');
			removeEvent(window, 'scroll.logoHeader');
			removeEvent(window, 'resize.logoHeader');
			removeEvent(window, 'message.acuityListener');
		};
	}, []);

	if (properties.siteInitiated === false || properties.siteClosed === true) return null;

	let receipt = false;
	if (location.type === 'RECEIPT' || location.type === 'RECEIPT_DEBUG') receipt = true;
	const { totals = {} } = checkout.selection;

	/*
	const changeLanguageHandler = (e) => {
		const { pagePaths } = this.props;
		const changeToLanguage = e.currentTarget.dataset.language;
		props.changeLanguage(changeToLanguage);
		if (changeToLanguage !== 'en') {
			props.goToPage(`/${changeToLanguage}/${pagePaths && pagePaths[changeToLanguage] ? pagePaths[changeToLanguage] : ''}`);
		} else {
			props.goToPage(`/${pagePaths && pagePaths[changeToLanguage] ? pagePaths[changeToLanguage] : ''}`);
		}
	};
	*/

	const toggleCartHandler = (e) => {
		if (e && typeof e !== 'boolean') {
			const link = e.currentTarget.getAttribute('href');
			if (link && (link.indexOf('#') === 0 || link.indexOf('selection') !== -1)) e.preventDefault();
		}
		const top = window.pageYOffset || document.documentElement.scrollTop;
		const menuOffsetTemp = offset(headerElement.current).top;

		setMenuPopupHeight(window.innerHeight - 40 - (top >= menuOffsetTemp ? 0 : (menuOffsetTemp || 0)));

		if (hasClass(document.body, 'u_addedToBag')) {
			removeClass(document.body, 'u_addedToBag');
		} else if (typeof e === 'boolean') {
			toggleCartOpen(e);
		} else {
			toggleCartOpen((prevState) => {
				if (prevState) return false;

				setMenuPopupHeight(window.innerHeight - 40 - (menuOffsetTemp - top <= 0 ? 0 : menuOffsetTemp - top));
				return true;
			});
		}
		/*
		if (this.props.checkout.klarnaUnloaded) {
			window.location = `${window.location.origin}/${this.props.setup.language}/selection`;
		}
		*/
	};

	const updateMenuPopupHeight = () => {
		const top = window.pageYOffset || document.documentElement.scrollTop;
		const menuOffsetTemp = offset(headerElement.current).top;
		setMenuPopupHeight(window.innerHeight - 40 - (top >= menuOffsetTemp ? 0 : (menuOffsetTemp || 0)));
	};

	const toggleAssistanceHandler = (e) => {
		if (e) e.preventDefault();
		toggleShowCountry(false);
		toggleShowAssistance((prevState) => !prevState);
	};

	const toggleCountryHandler = (e) => {
		if (e) e.preventDefault();
		toggleShowCountry((prevState) => !prevState);
		toggleShowAssistance(false);
	};

	const toggleSearchHandler = (e) => {
		if (e) e.preventDefault();
		const $input = e.currentTarget.parentNode.querySelector('input');

		toggleShowSearch((prevState) => {
			if (!prevState) {
				$input.focus();
			}

			toggleShowSearchSuggestions(!showSearch);
			return !showSearch;
		});
	};

	const toggleSearchSuggestionsHandler = (e) => {
		if (typeof e === 'boolean') {
			toggleShowSearchSuggestions(e);
			return;
		}
		if (e.type === 'focus') {
			toggleShowSearchSuggestions(true);
			return;
		}
		toggleShowSearchSuggestions((prevState) => !prevState);
	};

	const closeSearchHandler = () => {
		toggleShowSearch(false);
		toggleShowSearchSuggestions(false);
	};

	const countryChangeHandler = (e, value) => {
		if (typeof value === 'undefined') {
			// Link clicked get value
			value = e.currentTarget.dataset.country;
		}
		props.changeCountry(value);
		props.changeAddressInput('address', 'country', value);
		props.changeAddressInput('shippingAddress', 'country', value);
	};

	const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' && setup.countries[country].shipTo ? [...acc, { key: country, value: `${setup.countries[country].name} (${setup.countries[country].currency})` }] : [...acc]), []);

	return (
		<>
			{ setup.unableToSendToCountry && <Notification type="error" content={[{ name: 'unableToSendToCountry', text: setup.statics.unableToSendToCountry }]} setup={setup} /> }
			{
				location.type !== 'SELECTION' && (
					<>
						<Notification type="campaign" content={header.campaign} setup={setup} />
						<Notification type="popup" content={header.popup} setup={setup} />
						<CookiesNotification type="cookies" content={header.cookies} setup={setup} setCookieConsent={props.setCookieConsent} />
						<Navigation cartOpen={cartOpen} navigation={navigation} setup={setup} menuPopupHeight={menuPopupHeight} updateMenuPopupHeight={updateMenuPopupHeight} closeSearch={closeSearchHandler} countryChange={countryChangeHandler} />
						<div className={s.functions} ref={headerElement}>
							<div className={[s.functions_assistance, s.functions_assistance__nav, (showAssistance ? s.functions_assistance__open : null)].join(' ')}>
								<a className={s.functions_assistance_toggle} onClick={toggleAssistanceHandler} href="#toggle-assistance">
									<span className={s.functions_assistance_toggle_currency}>{ translate(setup, 'menuCustomerServiceTitle') }</span>
								</a>
								<button className={s.functions_assistance_screen} type="button" aria-label="Close assistance" onClick={toggleAssistanceHandler} />
								<div id="toggle-assistance" className={s.functions_assistance_inner}>
									<div className={s.assistance}>
										<span className={s.assistance_country_title}>{ translate(setup, 'menuCustomerServiceTitle') }</span>
										<div className={s.assistance_links}>
											{
												navigation.contact && navigation.contact.map((link) => {
													const uri = cleanInternalLinks(translate(setup, link, 'uri'));
													if (/(http|mailto|tel)/.test(uri)) {
														return <a className={s.assistance_link} href={uri} key={uri} target="_blank" rel="noopener noreferrer nofollow"><span>{ translate(setup, link, 'title') }</span></a>;
													}
													return <Link className={s.assistance_link} to={prefix(setup, uri)} key={uri}><span>{ translate(setup, link, 'title') }</span></Link>;
												})
											}
										</div>
									</div>
								</div>
							</div>

							<div className={[s.functions_assistance, s.functions_assistance__country, (showCountry ? s.functions_assistance__open : null)].join(' ')}>
								<a className={s.functions_assistance_toggle} onClick={toggleCountryHandler} href="#toggle-country">
									<img className={s.functions_assistance_toggle_icon} src={`/static/flags/${setup.country.toLowerCase()}.svg`} />
									<span className={s.functions_assistance_toggle_currency}>{ setup.currency }</span>
								</a>
								<button className={s.functions_assistance_screen} type="button" aria-label="Close assistance" onClick={toggleCountryHandler} />
								<div id="toggle-country" className={s.functions_assistance_inner}>
									<div className={s.assistance}>
										<div className={s.assistance_country}>
											<span className={s.assistance_country_title}>{ `${translate(setup, 'shippingTo')}: ${setup.countries[setup.country].name}` }</span>
											<div className={s.assistance_country_markets}>
												{
													setup.languages && Object.keys(setup.languages).filter((l) => setup.languages[l].iso3166).sort((a, b) => setup.languages[a].name.localeCompare(setup.languages[b].name)).map((l) => {
														const c = setup.countries[setup.languages[l].iso3166];
														return <Link data-country={c.country} className={[s.assistance_country_market, setup.languages[l].iso3166.toLowerCase() === setup.country.toLowerCase() ? s.assistance_country_market__active : null].join(' ')} to={prefix({ languages: setup.languages, language: l }, pageStatus?.paths?.[l])} key={l} onClick={countryChangeHandler}><span>{ `${c.name} (${c.currency})` }</span></Link>;
													})
												}
											</div>
											<div className={s.assistance_country_select}>
												<Select id="countryNavigationDesktop" variantClasses={['select__country', 'label__country']} selected={setup.country} options={countries} inputChange={countryChangeHandler} label={`${translate(setup, 'labelMoreCountries')}: `} />
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className={[s.functions_search, (showSearch ? s.functions_search__active : null)].join(' ')}>
								<a className={s.functions_search_toggle} onClick={toggleSearchHandler} href="#toggle-search">
									<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
										<circle cx="7.05882" cy="7.05882" r="6.55882" stroke="black" />
										<path d="M11.7646 12.2354L15.9999 16.4706" stroke="black" strokeLinecap="round" />
									</svg>
								</a>
								<div id="toggle-search" className={s.functions_search_form}>
									<SearchForm closeSearch={closeSearchHandler} toggleSearchSuggestionsHandler={toggleSearchSuggestionsHandler} showSearch={showSearch} showSearchSuggestions={showSearchSuggestions} />
								</div>
							</div>

							<Link className={[s.functions_selection, (!receipt && totals.totalQuantity ? s.functions_selection__selected : null)].join(' ')} to={prefix(setup, 'selection')} onClick={toggleCartHandler}>
								<svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M0.5 6.55566C0.5 5.17495 1.61929 4.05566 3 4.05566H13C14.3807 4.05566 15.5 5.17495 15.5 6.55566V15.6668C15.5 17.0475 14.3807 18.1668 13 18.1668H3C1.61929 18.1668 0.5 17.0475 0.5 15.6668V6.55566Z" />
									<path d="M4.94446 3.55556C4.94446 1.86802 6.31248 0.5 8.00001 0.5C9.68755 0.5 11.0556 1.86802 11.0556 3.55556V3.94444H4.94446V3.55556Z" />
								</svg>
								<span className={s.functions_selection_amount}>{ (!receipt && totals.totalQuantity) || '' }</span>
							</Link>

							<div className={[s.cart, cartOpen && !receipt ? s.cart__open : null].join(' ')} ref={cartElement}>
								<button className={s.cart_screen} type="button" aria-label="Close Cart" onClick={toggleCartHandler} />
								<div className={s.cart_inner}>
									<Cart maxHeight={menuPopupHeight} toggleCartHandler={toggleCartHandler} />
								</div>
							</div>
						</div>
					</>
				)
			}
			{
				location.type === 'SEARCH' && (
					<div className={[s.search, (location.type === 'SEARCH' ? s.search__active : null)].join(' ')}>
						<div className={s.searchScreen} />
						<SearchResults closeSearch={closeSearchHandler} />
					</div>
				)
			}
			{ grid && <div className={s.grid}><div /></div> }
		</>
	);
};

const mapState = ({ environment, checkout, location, pageStatus }) => {
	const { properties, navigation, setup, header } = environment;
	return { properties, navigation, setup, header, checkout, location, pageStatus };
};

export default hot(module)(connect(mapState, {
	removeNotification,
	resetFilter,
	changeCountry,
	changeAddressInput,
	changeLanguage,
	goToPage,
	clientInit,
	changeDevice,
	setCookieConsent,
})(Header));
